import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { useTranslation } from "react-i18next";
import Sidebar from "../components/Sidebar";
import { useCompany } from "../hooks/useCompany";

const API_URL = process.env.REACT_APP_API_URL;

const fmt = (n) =>
  n === 0 ? "—" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtMoney = (n) =>
  `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Convert raw salary + period to the weekly equivalent paid on a given entry
const salaryWeeklyPay = (wage, period) => {
  const w = Number(wage) || 0;
  if (period === "monthly") return w * 12 / 52;
  if (period === "yearly")  return w / 52;
  return w; // weekly
};

const salaryPeriodLabel = (period) => {
  if (period === "monthly") return "mo";
  if (period === "yearly")  return "yr";
  return "wk";
};

const PayrollHistory = () => {
  const auth = useAuth();
  const { t } = useTranslation();
  const { activeCompany } = useCompany();
  const [employees, setEmployees] = useState([]);
  const [payrollSummary, setPayrollSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [payrollHistory, setPayrollHistory] = useState([]);
  const [loadingPayroll, setLoadingPayroll] = useState(false);
  const [errorPayroll, setErrorPayroll] = useState(null);
  const [sort, setSort] = useState({ key: "name", dir: "asc" });

  useEffect(() => {
    if (!auth?.isAuthenticated || !activeCompany) { setLoading(false); return; }

    const token = auth.user?.access_token;
    const companyId = activeCompany.company_id;
    const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

    const fetchEmployees = fetch(`${API_URL}/employees?company_id=${companyId}`, { headers })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .catch((err) => { setError(`Failed to fetch employees: ${err}`); return []; });

    const fetchSummary = fetch(`${API_URL}/payroll_entries/summary?company_id=${companyId}`, { headers })
      .then((r) => (r.ok ? r.json() : {}))
      .catch(() => ({}));

    Promise.all([fetchEmployees, fetchSummary]).then(([emps, summary]) => {
      setEmployees(emps);
      setPayrollSummary(summary);
      setLoading(false);
    });
  }, [auth, activeCompany]);

  const fetchPayrollHistory = async (employeeId) => {
    setLoadingPayroll(true);
    setErrorPayroll(null);
    setPayrollHistory([]);

    try {
      const token = auth.user?.access_token;
      const companyParam = activeCompany ? `&company_id=${activeCompany.company_id}` : "";
      const response = await fetch(`${API_URL}/payroll_entries/employee/${employeeId}?_=1${companyParam}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 404) {
        setPayrollHistory([]);
      } else if (!response.ok) {
        throw new Error(`Failed to fetch payroll history: ${response.status}`);
      } else {
        const payrollData = await response.json();
        setPayrollHistory(payrollData.sort((a, b) => new Date(b.date) - new Date(a.date)));
      }
    } catch (err) {
      setErrorPayroll(err.message);
    } finally {
      setLoadingPayroll(false);
    }
  };

  const handleEmployeeClick = (employee) => {
    setSelectedEmployee(employee);
    fetchPayrollHistory(employee.employee_id);
  };

  const handleDeletePayroll = async (payrollId) => {
    const confirmDelete = window.confirm(t("delete_payroll_confirm"));
    if (!confirmDelete) return;

    try {
      const token = auth.user?.access_token;
      const response = await fetch(`${API_URL}/payroll_entries/${payrollId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete payroll: ${response.status}`);
      }

      setPayrollHistory((prevHistory) => prevHistory.filter((entry) => entry.payroll_id !== payrollId));
    } catch (err) {
      alert("Error deleting payroll: " + err.message);
    }
  };

  const toggleSort = (key) =>
    setSort((prev) => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));

  const sortedEmployees = useMemo(() => {
    const { key, dir } = sort;
    return [...employees].sort((a, b) => {
      if (key === "name") {
        const av = `${a.first_name} ${a.last_name}`.toLowerCase();
        const bv = `${b.first_name} ${b.last_name}`.toLowerCase();
        return dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      if (key === "wage") {
        const av = Number(a.wage) || 0;
        const bv = Number(b.wage) || 0;
        return dir === "asc" ? av - bv : bv - av;
      }
      const sa = payrollSummary[a.employee_id] || {};
      const sb = payrollSummary[b.employee_id] || {};
      const av = sa[key] ?? 0;
      const bv = sb[key] ?? 0;
      return dir === "asc" ? av - bv : bv - av;
    });
  }, [employees, payrollSummary, sort]);

  const sortIcon = (col) => (
    <span className={`ml-1 text-xs ${sort.key === col ? "text-gray-700" : "text-gray-300"}`}>
      {sort.key === col ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
    </span>
  );

  const handleSignOut = () => {
    auth.signOut();
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar handleSignOut={handleSignOut}>
      <div className="flex flex-col items-center justify-center flex-1 p-3 sm:p-4">
        <div className="bg-white shadow-lg rounded-lg p-4 sm:p-8 w-full max-w-5xl">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-3 sm:mb-4">{t("payroll_history_title")}</h1>
        <p className="text-gray-600 mb-4 sm:mb-6 text-sm">{t("payroll_history_subtitle")}</p>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10">
            <svg className="animate-spin h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span className="text-gray-400 text-sm">Loading employees…</span>
          </div>
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-300 text-sm">
                <thead>
                  <tr className="bg-gray-200 text-gray-700">
                    <th className="px-4 py-2 border text-left cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("name")}>
                      {t("col_employee")}{sortIcon("name")}
                    </th>
                    <th className="px-4 py-2 border text-left cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("wage")}>
                      {t("col_wage_header")}{sortIcon("wage")}
                    </th>
                    <th className="px-4 py-2 border text-right cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("last_2_weeks")}>
                      {t("col_last_2_weeks")}{sortIcon("last_2_weeks")}
                    </th>
                    <th className="px-4 py-2 border text-right cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("prev_month")}>
                      {t("col_prev_month")}{sortIcon("prev_month")}
                    </th>
                    <th className="px-4 py-2 border text-right cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("ytd")}>
                      {t("col_ytd")}{sortIcon("ytd")}
                    </th>
                    <th className="px-4 py-2 border text-right cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("prev_year")}>
                      {t("col_prev_year")}{sortIcon("prev_year")}
                    </th>
                    <th className="px-4 py-2 border"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedEmployees.map((employee) => {
                    const s = payrollSummary[employee.employee_id] || {};
                    return (
                      <tr key={employee.employee_id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 border font-medium">{employee.first_name} {employee.last_name}</td>
                        <td className="px-4 py-2 border text-gray-500">
                          {employee.is_salary
                            ? `${fmtMoney(employee.wage)}/${salaryPeriodLabel(employee.salary_period)}`
                            : `$${Number(employee.wage).toFixed(2)}/hr`}
                        </td>
                        <td className="px-4 py-2 border text-right tabular-nums">{fmt(s.last_2_weeks ?? 0)}</td>
                        <td className="px-4 py-2 border text-right tabular-nums">{fmt(s.prev_month   ?? 0)}</td>
                        <td className="px-4 py-2 border text-right tabular-nums font-medium">{fmt(s.ytd        ?? 0)}</td>
                        <td className="px-4 py-2 border text-right tabular-nums text-gray-500">{fmt(s.prev_year  ?? 0)}</td>
                        <td className="px-4 py-2 border text-center">
                          <button
                            className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 text-xs"
                            onClick={() => handleEmployeeClick(employee)}
                          >
                            {t("view_history")}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {sortedEmployees.map((employee) => {
                const s = payrollSummary[employee.employee_id] || {};
                return (
                  <div key={employee.employee_id} className="border border-gray-200 rounded-lg p-3 bg-white">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-semibold text-gray-800 text-sm">{employee.first_name} {employee.last_name}</p>
                        <p className="text-xs text-gray-500">
                          {employee.is_salary
                            ? `${fmtMoney(employee.wage)}/${salaryPeriodLabel(employee.salary_period)}`
                            : `$${Number(employee.wage).toFixed(2)}/hr`}
                        </p>
                      </div>
                      <button
                        className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 text-xs flex-shrink-0"
                        onClick={() => handleEmployeeClick(employee)}
                      >
                        {t("view_history")}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-500">{t("col_last_2_weeks")}</span>
                        <span className="tabular-nums">{fmt(s.last_2_weeks ?? 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">{t("col_prev_month")}</span>
                        <span className="tabular-nums">{fmt(s.prev_month ?? 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">{t("col_ytd")}</span>
                        <span className="tabular-nums font-medium">{fmt(s.ytd ?? 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">{t("col_prev_year")}</span>
                        <span className="tabular-nums text-gray-500">{fmt(s.prev_year ?? 0)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        <Link to="/" className="block mt-6">
          <button className="w-full bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition">
            {t("back_to_dashboard_btn")}
          </button>
        </Link>
      </div>
      {/* Payroll History Modal */}
      {selectedEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              {t("payroll_history_for", { name: `${selectedEmployee.first_name} ${selectedEmployee.last_name}` })}
            </h2>
            {loadingPayroll ? (
              <div className="flex items-center justify-center gap-2 py-10">
                <svg className="animate-spin h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span className="text-gray-400 text-sm">Loading payroll history…</span>
              </div>
            ) : errorPayroll ? (
              <p className="text-red-500">{errorPayroll}</p>
            ) : payrollHistory.length === 0 ? (
              <p className="text-gray-600">{t("no_payroll_records")}</p>
            ) : (
              <div className="overflow-y-auto max-h-96"> {/* Increased height */}
                <table className="w-full bg-white border border-gray-300">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="px-4 py-2 border w-1/4">{t("col_date")}</th>
                      <th className="px-4 py-2 border w-1/4">{t("col_hours")}</th>
                      <th className="px-4 py-2 border w-1/4">{t("col_total_pay")}</th>
                      <th className="px-4 py-2 border w-1/4">{t("col_actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payrollHistory.map((payroll) => {
                      const isSal = payroll.is_salary;
                      const period = selectedEmployee?.salary_period || "weekly";
                      const displayPay = isSal
                        ? salaryWeeklyPay(payroll.wage, period) / 5
                        : payroll.total_pay;
                      return (
                      <tr key={payroll.payroll_id} className="hover:bg-gray-100">
                        <td className="px-4 py-2 border whitespace-nowrap">{payroll.date}</td>
                        <td className="px-4 py-2 border text-gray-500">
                          {isSal ? `${fmtMoney(payroll.wage)}/${salaryPeriodLabel(period)}` : payroll.hours_worked}
                        </td>
                        <td className="px-4 py-2 border tabular-nums">{fmtMoney(displayPay)}</td>
                        <td className="px-4 py-2 border">
                          <button
                            className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                            onClick={() => handleDeletePayroll(payroll.payroll_id)}
                          >
                            {t("delete")}
                          </button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex justify-between mt-4">
              <button
                className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500"
                onClick={() => setSelectedEmployee(null)}
              >
                {t("close")}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
      </Sidebar>
    </div>
  );
};

export default PayrollHistory;
